/**
 * LoadingSpinner Component
 *
 * Animated loading indicator with optional text.
 *
 * Props:
 * - size: Size variant (small, medium, large)
 * - text: Optional loading message
 * - className: Custom CSS classes
 * - color: Color theme (default: blue)
 * - inline: Display inline vs centered
 */

import { COPY } from '../copy';

export default function LoadingSpinner({
  size = 'medium',
  text = '',
  className = '',
  color = 'blue',
  inline = false,
}) {
  // Size mappings for spinner
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12',
  };

  // Color mappings
  const colorClasses = {
    blue: 'border-blue-500',
    gray: 'border-gray-500',
    indigo: 'border-indigo-500',
  };

  const spinnerSize = sizeClasses[size] || sizeClasses.medium;
  const spinnerColor = colorClasses[color] || colorClasses.blue;

  // Build aria-label
  const ariaLabel = text ? COPY.loading.withText(text) : COPY.loading.default;

  // Container classes
  const containerClasses = inline
    ? 'inline-flex items-center gap-2'
    : 'flex flex-col items-center justify-center gap-2';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      className={containerClasses}
    >
      <div
        data-testid="loading-spinner"
        className={`${spinnerSize} ${spinnerColor} border-4 border-t-transparent rounded-full animate-spin ${className}`}
        aria-label={ariaLabel}
      />
      {text && <p className="text-sm text-gray-600">{text}</p>}
    </div>
  );
}
